import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';

import {
  fetchFavoriteProfessionals,
  getAllProfessionals,
  inviteFavoriteToJob,
} from '../actions';
import { Colors } from '../themes/colors';
import searchIcon from '../images/search-icon.svg';
import closeIcon from '../images/close-icon.svg';
import checkSent from '../images/check-sent.svg';
import { createLoadingSelector } from '../api/selectors';
import CustomSpinner from '../commonComponents/CustomSpinner';

export default function InviteFavorites({ setShowInviteFavoritesPopup }) {
  const dispatch = useDispatch();

  const [selectedFavorites, setSelectedFavorites] = useState([]);
  const [professionalNameFilter, setProfessionalNameFilter] = useState('');

  const loadingSelector = createLoadingSelector([
    'FETCH_FAVORITE_PROFESSIONALS',
  ]);
  const isLoading = useSelector((state) => loadingSelector(state));

  const job = useSelector((state) => state.job.job);

  const favoritesList = useSelector(
    (state) => state.user.favoriteProfessionals,
  );
  const filteredProfessionals = favoritesList?.filter((professional) => professional?.fullname
    ?.toLowerCase()
    ?.includes(professionalNameFilter?.toLowerCase()));
  const allProfessions = useSelector((state) => state.user.allProfessions);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    dispatch(fetchFavoriteProfessionals(job?.id));
    dispatch(getAllProfessionals());

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    setSelectedFavorites([]);
  }, [job]);

  const circleVariants = {
    checked: {
      opacity: 1,
    },
    unchecked: {
      opacity: 0,
    },
  };

  const tickVariants = {
    checked: {
      opacity: 1,
      strokeDashoffset: 20,
    },
    unchecked: {
      opacity: 0,
      strokeDashoffset: 50,
    },
  };

  const renderFavoriteProfessional = (item) => {
    const selectedUser = selectedFavorites.includes(item.id);
    const userProfession = item.professions?.includes(job?.profession)
      ? allProfessions?.find(({ code }) => job?.profession === code)?.name
      : allProfessions?.find(({ code }) => item.professions[0] === code)?.name;

    const inviteSent = job?.favoriteInvites?.some(
      ({ professional }) => professional === item.id,
    );

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        layout
        onClick={() => {
          if (inviteSent) return;

          if (selectedUser) {
            setSelectedFavorites(
              selectedFavorites.filter((userId) => userId !== item.id),
            );
          } else {
            setSelectedFavorites([...selectedFavorites, item.id]);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0px',
          borderBottom: `1px solid ${Colors.neutral_100}`,
          cursor: inviteSent ? 'auto' : 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={item.profileImage}
            style={{
              width: 65,
              height: 65,
              marginRight: 13,
              borderRadius: '50%',
              boxShadow: '0px 6px 10px 2px rgba(0, 0, 0, 0.15)',
            }}
            alt="profile_image"
          />

          <div>
            <div
              style={{
                color: Colors.neutral_600,
                fontSize: 20,
                fontFamily: 'Nunito',
                fontWeight: 'bold',
              }}
            >
              {item.fullname}
            </div>

            <div
              style={{
                color: Colors.neutral_500,
                fontSize: 14,
                fontFamily: 'Nunito',
              }}
            >
              {item.isAvailable
                ? userProfession
                : 'Currently unavailable'}
            </div>
          </div>
        </div>

        {inviteSent ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 5,
            }}
          >
            <img
              src={checkSent}
              style={{ width: 24, height: 24 }}
              alt="check_sent"
            />

            <p
              style={{
                color: Colors.neutral_400,
                fontSize: 14,
                fontFamily: 'Nunito',
              }}
            >
              Sent
            </p>
          </motion.div>
        ) : (
          <motion.svg
            animate={selectedUser ? 'checked' : 'unchecked'}
            initial="unchecked"
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
          >
            <motion.rect x="1" y="1" width="18" height="18" rx="9" stroke="#A9B3B7" strokeWidth="2" />
            <motion.circle cx="10" cy="10" r={10} fill="#A96ABE" variants={circleVariants} />

            <motion.path
              d="M6.5 10.5L8.86667 13L14 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDashoffset={20}
              strokeDasharray={40}
              variants={tickVariants}
            />
          </motion.svg>
        )}
      </motion.div>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        position: 'fixed',
        overflow: 'hidden',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        zIndex: 10,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'relative',
          backgroundColor: Colors.white,
          alignContent: 'center',
          width: '30%',
          height: !isLoading && !favoritesList?.length ? 400 : 550,
          minWidth: 400,
          padding: '35px',
          borderRadius: 25,
          display: 'flex',
          flexDirection: 'column',
          marginBottom: 50,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)',
          shadowColor: Colors.neutral_400,
          shadowOpacity: 0.3,
          shadowOffset: {
            height: 3,
            width: 0,
          },
        }}
      >
        <img
          onClick={() => setShowInviteFavoritesPopup(false)}
          src={closeIcon}
          style={{
            width: 24,
            height: 24,
            position: 'absolute',
            top: 20,
            left: '92%',
            cursor: 'pointer',
          }}
          alt="close_icon"
        />

        {!isLoading && !favoritesList?.length ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '20px 30px',
            }}
          >
            <div
              style={{
                color: Colors.neutral_600,
                fontSize: 18,
                fontFamily: 'Nunito',
                textAlign: 'center',
                fontWeight: 'bold',
                marginBottom: 30,
              }}
            >
              No Favorites Available
            </div>

            <div
              style={{
                color: Colors.neutral_600,
                fontSize: 16,
                fontFamily: 'Nunito',
                marginBottom: 30,
              }}
            >
              Sorry! We were not able to find Favorites that are available for
              this shift. This may have happened for the following reasons:
            </div>

            <div
              style={{
                width: '100%',
                color: Colors.neutral_600,
                fontSize: 16,
                fontFamily: 'Nunito',
                fontWeight: 'bold',
                marginBottom: 30,
              }}
            >
              <div>1. You have not added a Favorite yet</div>
              <br />
              <div>2. Your Favorites are not available for this shift</div>
            </div>

            <div
              style={{
                color: Colors.neutral_600,
                fontSize: 16,
                fontFamily: 'Nunito',
                marginBottom: 30,
              }}
            >
              To prevent this from happening in the future, we recommend that
              you add more Favorites to your list (max = 12).
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                color: Colors.neutral_600,
                fontSize: 18,
                fontFamily: 'Nunito',
                textAlign: 'center',
                fontWeight: 'bold',
                marginBottom: 30,
              }}
            >
              Select Favorites
            </div>

            <div style={{ position: 'relative', width: '100%' }}>
              <img
                src={searchIcon}
                style={{
                  width: 22,
                  height: 22,
                  position: 'absolute',
                  top: '25%',
                  left: 10,
                }}
                alt="search_icon"
              />

              <input
                type="text"
                placeholder="Search"
                onChange={(event) => setProfessionalNameFilter(event.target.value)}
                style={{
                  margin: 0,
                  width: '88%',
                  paddingLeft: 40,
                  backgroundColor: Colors.neutral_50,
                  borderRadius: 10,
                  fontFamily: 'Nunito',
                  boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.25) inset',
                }}
              />
            </div>

            <div
              style={{
                height: '70%',
                overflowY: 'auto',
                padding: '0px 20px',
                marginTop: 20,
              }}
            >
              {isLoading && <CustomSpinner isLoading />}

              {!isLoading && (
                <AnimatePresence>
                  {filteredProfessionals?.map((item) => renderFavoriteProfessional(item))}
                </AnimatePresence>
              )}
            </div>

            <button
              type="button"
              className="green-button"
              disabled={!selectedFavorites.length}
              style={{
                margin: 0,
                marginTop: 20,
                alignSelf: 'center',
                width: 150,
                backgroundColor: selectedFavorites.length
                  ? Colors.primary_500
                  : Colors.neutral_200,
              }}
              onClick={() => dispatch(inviteFavoriteToJob(job?.id, selectedFavorites))}
            >
              <p className="white-text-18">Send</p>
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
